import { appPath } from '../ApiPaths';
import { patchFormData, patchJson } from './httpClient';
import { IApp } from '../types/App.model';

/**
 * App Update Context Types
 * Defines what type of update is being performed
 */
export type AppUpdateContext =
  | 'basic' // AppAdmin: name, logo, description
  | 'metadata' // AppAdmin: metadata table
  | 'connector' // AppAdmin: connector selection
  | 'limits' // MasterAdmin: user/model limits
  | 'platforms' // MasterAdmin: platform selection
  | 'full'; // MasterAdmin: all fields

/**
 * Unified App Update Response
 */
export interface UnifiedAppUpdateResponse {
  message: string;
  app: IApp;
  context?: AppUpdateContext;
  updatedFields?: string[];
}

/**
 * Unified App API Service
 * Provides a clean, type-safe interface for all app update operations
 */
export class UnifiedAppAPI {
  /**
   * Main unified app update method
   * Automatically detects context based on provided fields
   *
   * @param appId The ID of the app to update
   * @param updates Object containing updates to apply
   * @param logoFile Optional logo file to upload
   * @returns Promise with updated app data
   */
  static async updateApp(
    appId: string,
    updates: Partial<IApp>,
    logoFile?: File
  ): Promise<UnifiedAppUpdateResponse> {
    const formData = new FormData();

    // Add text fields to form data
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'plattforms' && Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    // Add logo file if provided
    if (logoFile) {
      formData.append('logo', logoFile);
    }

    return patchFormData<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}`, formData);
  }

  /**
   * Update app basic information (name, logo, description)
   * AppAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param updates Basic app fields to update
   * @param logoFile Optional logo file to upload
   * @returns Promise with updated app data
   */
  static async updateBasic(
    appId: string,
    updates: {
      appName?: string;
      summaryDescription?: string;
    },
    logoFile?: File
  ): Promise<UnifiedAppUpdateResponse> {
    const formData = new FormData();

    if (updates.appName) formData.append('appName', updates.appName);
    if (updates.summaryDescription) formData.append('summaryDescription', updates.summaryDescription);
    if (logoFile) formData.append('logo', logoFile);

    return patchFormData<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/basic`, formData);
  }

  /**
   * Update app metadata
   * AppAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param metadata The metadata object to update
   * @returns Promise with updated app data
   */
  static async updateMetadata(appId: string, metadata: any): Promise<UnifiedAppUpdateResponse> {
    return patchJson<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/metadata`, { metadata });
  }

  /**
   * Update app connector selection
   * AppAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param connectorSelection The connector selection object
   * @returns Promise with updated app data
   */
  static async updateConnector(
    appId: string,
    connectorSelection: any
  ): Promise<UnifiedAppUpdateResponse> {
    return patchJson<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/connector`, {
      two_part_connector_selection: connectorSelection,
    });
  }

  /**
   * Update app limits (user limit, model limit)
   * MasterAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param limits Object containing userLimit and/or modelLimit
   * @returns Promise with updated app data
   */
  static async updateLimits(
    appId: string,
    limits: {
      userLimit?: number;
      modelLimit?: number;
    }
  ): Promise<UnifiedAppUpdateResponse> {
    return patchJson<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/limits`, limits);
  }

  /**
   * Update app platforms
   * MasterAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param platforms Array of supported platforms
   * @returns Promise with updated app data
   */
  static async updatePlatforms(appId: string, platforms: string[]): Promise<UnifiedAppUpdateResponse> {
    return patchJson<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/platforms`, {
      plattforms: platforms,
    });
  }

  /**
   * Update app with full permissions
   * MasterAdmin permission required
   *
   * @param appId The ID of the app to update
   * @param updates Object containing any app fields to update
   * @returns Promise with updated app data
   */
  static async updateFull(appId: string, updates: Partial<IApp>): Promise<UnifiedAppUpdateResponse> {
    return patchJson<UnifiedAppUpdateResponse>(`${appPath}apps/${appId}/full`, updates);
  }

  /**
   * Context-specific update method
   * Explicitly specify the update context
   *
   * @param appId The ID of the app to update
   * @param updates Object containing updates to apply
   * @param context The update context
   * @param logoFile Optional logo file for basic updates
   * @returns Promise with updated app data
   */
  static async updateWithContext(
    appId: string,
    updates: Partial<IApp>,
    context: AppUpdateContext,
    logoFile?: File
  ): Promise<UnifiedAppUpdateResponse> {
    switch (context) {
      case 'basic':
        return this.updateBasic(
          appId,
          {
            appName: updates.appName,
            summaryDescription: updates.summaryDescription,
          },
          logoFile
        );

      case 'metadata':
        return this.updateMetadata(appId, updates.metadata);

      case 'connector':
        return this.updateConnector(appId, updates.two_part_connector_selection);

      case 'limits':
        return this.updateLimits(appId, {
          userLimit: updates.userLimit,
          modelLimit: updates.modelLimit,
        });

      case 'platforms':
        return this.updatePlatforms(appId, updates.plattforms || []);

      case 'full':
        return this.updateFull(appId, updates);

      default:
        throw new Error(`Unknown update context: ${context}`);
    }
  }
}
